import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useOnboardingStore } from '../../stores/onboardingStore';
import {
  isValidPaymentPlan,
  parsePlanIdFromUrl,
  getCurrentPlanFromPaymentStatus,
  effectivePlanStatus,
} from '../../lib/services/payment/planUtils';
import type { UserProfile } from '../../types/auth';
import {
  getNextRegistrationRoute,
  getApplicableFlowRoutes,
  REGISTRATION_FLOW_ROUTES,
} from '@constants/userFlow';
import { FullScreenLoading } from '@components/common/Loading';

interface NewUserProtectedRouteProps {
  children: React.ReactNode;
  requiredPermissions?: string[];
}

// Helper: does the user have a Shopify installation? Shopify users skip the
// eCommerce-platform picker in the Onboarding 2.0 flow (their platform is known
// from the App Store OAuth install).
const hasShopifyInstallation = (user: UserProfile | null): boolean => {
  return user?.installations?.some(installation => installation.platform === 'shopify') ?? false;
};

// Check if user has paid status. Credit-plan (ai_*) users keep
// payment.paymentStatus = "unpaid" — their paid signal is creditPlanCode — so we
// resolve the effective plan: a non-null paid plan (any ai_* paid tier or a legacy
// paid status) counts as paid; `free` / unpaid / expired do not.
const isPaidUser = (user: UserProfile | null): boolean => {
  return getCurrentPlanFromPaymentStatus(effectivePlanStatus(user) ?? '') !== null;
};

/**
 * The route this user belongs on in the Onboarding 2.0 registration flow, or
 * null when every applicable step is complete (→ send them into the app).
 * Payment is treated as satisfied for paid / free-trial users — billing is
 * handled by the existing plan/payment pages, not re-gated here.
 */
const resolveRequiredRoute = (user: UserProfile): string | null => {
  const stepName = user.stepName || '';
  const hasShopify = hasShopifyInstallation(user);
  const treatPaymentDone = isPaidUser(user) || !!user.inFreeTrial;
  return getNextRegistrationRoute(stepName, hasShopify, treatPaymentDone);
};

// The plan/payment pages form one cluster — while the user's required step is
// plan-or-payment, allow them on either (plan-details redirects into payment).
const PLAN_PAYMENT_ROUTES = ['/plan-details', '/payment'];

const isRouteAllowed = (
  requiredRoute: string,
  currentPath: string,
  applicableRoutes: string[]
): boolean => {
  if (currentPath === requiredRoute) return true;
  if (PLAN_PAYMENT_ROUTES.includes(requiredRoute) && PLAN_PAYMENT_ROUTES.includes(currentPath)) {
    return true;
  }
  // Allow revisiting an EARLIER (already-completed) step — this is what makes the
  // Back button work. Only forward-skipping past the required step is blocked.
  const curIdx = applicableRoutes.indexOf(currentPath);
  const reqIdx = applicableRoutes.indexOf(requiredRoute);
  if (curIdx !== -1 && reqIdx !== -1 && curIdx < reqIdx) return true;
  return false;
};

// Check if payment access is valid (a real plan must be selected to reach /payment)
const isValidPaymentAccess = (location: ReturnType<typeof useLocation>): boolean => {
  if (location.pathname !== '/payment') return false;
  const searchParams = new URLSearchParams(location.search);
  const planParam = searchParams.get('plan');
  if (!planParam) return false;
  const planId = parsePlanIdFromUrl(planParam);
  return planId ? isValidPaymentPlan(planId) : false;
};

// Check if the current path is an external authentication path
const isExternalAuthPath = (pathname: string): boolean => {
  return pathname === '/auth/external-login' || pathname === '/auth/external-register';
};

export function NewUserProtectedRoute({ children }: NewUserProtectedRouteProps) {
  const { isAuthenticated, user, isLoading, isHydrated, isExternalIntegrationFlow } =
    useAuthStore();
  const { initializeFromUser } = useOnboardingStore();
  const [isInitializing, setIsInitializing] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  // Initialize onboarding store when user is available.
  useEffect(() => {
    if (user && isAuthenticated) {
      initializeFromUser();
    }
  }, [user, isAuthenticated, initializeFromUser]);

  // Root-route handoff: '/' has no page of its own — send the user to their
  // current onboarding step, or into the app when registration is complete.
  useEffect(() => {
    if (!isHydrated || isLoading || !isAuthenticated || !user) return;

    if (location.pathname === '/') {
      const requiredRoute = resolveRequiredRoute(user);
      navigate(requiredRoute ?? '/home', { replace: true });
      /*
       * 🔴 [2026-09-23 · YepaiSass-React#1670] 这条分支原来 navigate 完直接 return,
       * **不收 `isInitializing`** —— 而下面的渲染门读的就是它,于是落到 `/` 那一刻起屏幕
       * 是全屏「Preparing your dashboard...」,要等 pathname 变了、effect 重跑到下面才收。
       * 这一段正是商家「点完连接卡在转圈、只好硬刷新」的那一段(PRD Infra 1.5 #22 Case 2),
       * 叠上 #1669 那三个定时器之后会被拉长。
       *
       * <p>`/` 是一次交接,不是一次初始化:这里没有东西需要被初始化,只是读 user 决定去哪。
       *
       * <p>收掉之后,`/` 这一帧会走到下面的渲染期判断 —— 那里有同一套规则(没做完的
       * onboarding → `<Navigate to={requiredRoute}>`,做完了 → children,也就是 `/` 的 index
       * `<Navigate to="/home">`),和这里的 navigate 去的是同一个地方。唯一会让它们分叉的是
       * 外部集成旁路开关还开着的那一刻(渲染期直接放行 children),而那正是 #1669 修掉的:
       * 激活之后的跳转现在一定发生在旁路关闭之后。两个 issue 必须一起修。
       */
      setIsInitializing(false);
      return;
    }

    if (isExternalAuthPath(location.pathname)) {
      setIsInitializing(false);
      return;
    }

    setIsInitializing(false);
  }, [isHydrated, isLoading, isAuthenticated, user, location.pathname, navigate]);

  // Core loading states.
  if (isLoading || !isHydrated || (isAuthenticated && isInitializing)) {
    return (
      <FullScreenLoading
        variant="gradient"
        title="Loading YepAI"
        subtitle={
          !isHydrated
            ? 'Initializing application...'
            : !isAuthenticated
              ? 'Checking authentication...'
              : 'Preparing your dashboard...'
        }
      />
    );
  }

  // Redirect to login if not authenticated.
  if (!isAuthenticated) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // External integration / auth flows bypass onboarding gating.
  if (isExternalIntegrationFlow || isExternalAuthPath(location.pathname)) {
    return <>{children}</>;
  }

  if (user) {
    const requiredRoute = resolveRequiredRoute(user);

    // Registration complete → the app is open; keep them out of the onboarding
    // step pages (a stale bookmark shouldn't drop them back into the funnel).
    if (requiredRoute === null) {
      if (REGISTRATION_FLOW_ROUTES.includes(location.pathname)) {
        return <Navigate to="/home" replace />;
      }
      return <>{children}</>;
    }

    // Paid users can't sit on the plan page — their required step is past it.
    if (location.pathname === '/plan-details' && isPaidUser(user)) {
      return <Navigate to={requiredRoute} replace />;
    }

    // /payment needs a valid selected plan in the query.
    if (
      requiredRoute === '/payment' &&
      location.pathname === '/payment' &&
      !isValidPaymentAccess(location)
    ) {
      return <Navigate to="/plan-details" replace />;
    }

    // Off their required step → send them to it (unless it's an earlier
    // completed step they're revisiting via Back — that's allowed).
    const applicableRoutes = getApplicableFlowRoutes(
      hasShopifyInstallation(user),
      isPaidUser(user) || !!user.inFreeTrial
    );
    if (!isRouteAllowed(requiredRoute, location.pathname, applicableRoutes)) {
      return <Navigate to={requiredRoute} replace />;
    }
  }

  return <>{children}</>;
}
